import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { DecimalString } from '@efacture/web-billing/lib/decimal';
import {
  CONTRACT_TYPES,
  CONTRACT_TYPE_LABELS as ACCOUNTING_CONTRACT_TYPE_LABELS,
  CONTRACT_TYPE_OPTION_LABELS,
  type ContractType as AccountingContractType,
} from '@efacture/accounting';

// ─────────────────────────────────────────────────────────────
// Types (miroir des modèles Prisma — les Decimal arrivent en string)
// Modules M3 (RH : employés & congés) + M4 (Paie) — ERP Suite
// Spec: docs/superpowers/specs/2026-07-11-erp-suite-roadmap.md
// ─────────────────────────────────────────────────────────────

export type ContractType = AccountingContractType;

/**
 * Nature d'exonération IUTS (CGI art. 106) portée par une indemnité —
 * encadre le plafond d'exonération (logement 75 k, fonction 50 k,
 * transport 30 k FCFA/mois). Optionnelle ; absente = indemnité ordinaire.
 */
export type AllowanceNature = 'LOGEMENT' | 'FONCTION' | 'TRANSPORT' | 'AUTRE';

/**
 * Catégorie professionnelle (CGI art. 111) — pilote le taux d'abattement
 * forfaitaire pour frais professionnels (25 % standard, 20 % pour les
 * catégories supérieures P/A/B).
 */
export type ProfessionalCategory = 'STANDARD' | 'SUPERIEURE';

/** Prime/indemnité récurrente du contrat (Employee.allowances Json). */
export interface EmployeeAllowance {
  label: string;
  amount: number;
  /** Imposable à l'IUTS (assiette IUTS uniquement). */
  taxable: boolean;
  /**
   * Nature d'exonération IUTS (CGI art. 106) — OBLIGATOIRE dès que la
   * prime est imposable : « AUTRE » signifie « aucune exonération »,
   * l'absence de valeur n'est pas un choix (le serveur la refuse).
   */
  nature?: AllowanceNature;
  /**
   * Frais ayant un caractère de remboursement — seule exclusion de
   * l'assiette CNSS (loi n°004-2021/AN art. 10) ; sort aussi de
   * l'assiette TPA (CGI art. 228-1).
   */
  expenseReimbursement?: boolean;
}

export interface Employee {
  id: string;
  /** EMP-NNNN — attribué par séquence atomique, immuable. */
  matricule: string;
  firstName: string;
  lastName: string;
  /** Personal fields (email, phone, address): absent without data:personal. */
  email?: string | null;
  phone?: string | null;
  position: string;
  department: string | null;
  contractType: ContractType;
  hireDate: string;
  endDate: string | null;
  /** Pay fields: absent without payroll:read (#533). */
  cnssNumber?: string | null;
  ifu?: string | null;
  paymentMethod: 'VIREMENT' | 'CHEQUE' | 'ESPECES' | 'MOBILE_MONEY';
  bankName: string | null;
  /** `****` + last 4 in a list, and in a detail without payroll:read. */
  bankAccount: string | null;
  baseSalary?: DecimalString;
  allowances?: EmployeeAllowance[] | null;
  /** Charges de famille (CGI art. 113) — entier 0 à 4, réduction sur l'impôt. */
  familyCharges: number;
  /** Catégorie professionnelle (CGI art. 111) — abattement 25 % ou 20 %. */
  professionalCategory: ProfessionalCategory;
  /** CGI art. 117, 1°. Absent without data:personal. */
  address?: string | null;
  /** CGI art. 117, 4°: children only (spouse excluded); null when not entered. */
  dependentChildren: number | null;
  costCenterId: string | null;
  userId: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export type LeaveType =
  | 'CONGE_PAYE'
  | 'MALADIE'
  | 'MATERNITE'
  | 'SANS_SOLDE'
  | 'AUTRE';

export type LeaveStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

export interface LeaveRequest {
  id: string;
  employeeId: string;
  type: LeaveType;
  startDate: string;
  endDate: string;
  /** Jours OUVRÉS lundi→vendredi (Decimal 5,1 — string en JSON). */
  days: DecimalString;
  status: LeaveStatus;
  /** Absent for a sick or maternity leave without data:health. */
  reason?: string | null;
  approvedById: string | null;
  decidedAt: string | null;
  createdAt: string;
  /** Identité minimale incluse par la liste et le détail. */
  employee: {
    id: string;
    matricule: string;
    firstName: string;
    lastName: string;
  };
}

export interface LeaveBalance {
  employeeId: string;
  year: number;
  /** Droit annuel — paramètre de paie CONGES_PAYES_JOURS_ANNUELS du tenant. */
  entitlementDays: number;
  /** Days carried over from earlier years (imported opening position). */
  carriedOverDays: number;
  /** Days taken in the year before the cutover (imported opening position). */
  openingTakenDays: number;
  /** Paid leave approved here, counted in the year of its start date. */
  approvedDays: number;
  /** openingTakenDays + approvedDays. */
  takenDays: number;
  /** entitlementDays + carriedOverDays - takenDays. */
  remainingDays: number;
  /**
   * Note « à vérifier avec votre conseil » du paramètre (null si la
   * valeur a été vérifiée — note effacée dans les paramètres de paie).
   */
  entitlementNote: string | null;
}

/** IMPORTED: a month paid by the former payroll system, read-only here. */
export type PayrollRunStatus = 'DRAFT' | 'VALIDATED' | 'PAID' | 'IMPORTED';

export interface PayrollRun {
  id: string;
  year: number;
  month: number;
  status: PayrollRunStatus;
  totalGross: DecimalString;
  totalNet: DecimalString;
  totalEmployerCharges: DecimalString;
  /** Écriture comptable OD PAIE-{année}-{MM} (posée à la validation). */
  entryId: string | null;
  validatedAt: string | null;
  validatedById: string | null;
  createdById: string;
  createdAt: string;
  updatedAt: string;
}

/** Tranche du barème progressif IUTS ({ upTo: null } = dernière tranche). */
export interface IutsBracket {
  upTo: number | null;
  /** Taux fraction 0–1 (0.121 = 12,1 %). */
  rate: number;
}

/** Trace de calcul IUTS par tranche (audit du bulletin). */
export interface IutsBracketTrace {
  from: number;
  upTo: number | null;
  rate: number;
  taxableInBracket: number;
  tax: number;
}

/** Prime ponctuelle du mois (éditable en brouillon). */
export interface PayslipOneOffBonus {
  label: string;
  amount: number;
  taxable: boolean;
}

/** Retenue ponctuelle du mois (avance, opposition…). */
export interface PayslipOneOffDeduction {
  label: string;
  amount: number;
}

/** Trace persistée dans PayrollSlip.details (audit du calcul). */
export interface PayrollSlipDetails {
  calcul?: {
    taxableGross: number;
    cnssBase: number;
    abattement: number;
    iutsBrackets: IutsBracketTrace[];
  };
  oneOffBonuses?: PayslipOneOffBonus[];
  /** Retenues réglementaires sans colonne dédiée — déjà dans netPay. */
  fsp?: number;
  amuEmployee?: number;
  parametres?: {
    cnssEmployeeRate: number;
    cnssEmployerRate: number;
    cnssCeiling: number | null;
    iutsAbattementRate: number;
    iutsBrackets: IutsBracket[];
    tpaRate: number;
    /** Notes « à vérifier » des paramètres utilisés (code → note). */
    notes: Record<string, string>;
  };
  parametresAVerifier?: boolean;
}

export interface PayrollSlipEmployee {
  id: string;
  matricule: string;
  firstName: string;
  lastName: string;
  position: string;
  department: string | null;
  cnssNumber: string | null;
}

export interface PayrollSlip {
  id: string;
  runId: string;
  employeeId: string;
  gross: DecimalString;
  cnssEmployee: DecimalString;
  iuts: DecimalString;
  /** Retenues ponctuelles [{label, amount}] (Json). */
  otherDeductions: PayslipOneOffDeduction[] | null;
  netPay: DecimalString;
  employerCnss: DecimalString;
  /** Autres charges patronales [{label, amount}] (TPA…). */
  employerOther: PayslipOneOffDeduction[] | null;
  details: PayrollSlipDetails | null;
  employee: PayrollSlipEmployee;
}

export type PayrollRunDetail = PayrollRun & { slips: PayrollSlip[] };

/** A visa event (hr-visa) — accountant's countersignature BEFORE validate(). */
export type PayrollRunEventKind = 'VISAED' | 'VISA_REFUSED';

export interface PayrollRunEvent {
  id: string;
  kind: PayrollRunEventKind;
  /** Written reason — required for VISA_REFUSED, null for VISAED. */
  reason: string | null;
  actorId: string;
  /** Displayable actor name (null if the user was deleted). */
  actorName: string | null;
  createdAt: string;
}

/** Paramètre de paie résolu pour une période (valeur datée retenue). */
export interface ResolvedPayrollParameter {
  code: string;
  value: unknown;
  effectiveFrom: string;
  /** Note « à vérifier avec votre conseil » (null = valeur vérifiée). */
  note: string | null;
  /**
   * Référence légale EXACTE du texte source (registre fiscal P0.2 —
   * docs/fiscal/), telle que persistée sur PayrollParameter.legalReference.
   * null = aucune référence enregistrée.
   */
  legalReference: string | null;
  /**
   * ★ Ce paramètre gouverne-t-il les bulletins de cette période ?
   * DÉRIVÉ côté serveur du mode d'assiette IUTS résolu (constat F5) :
   * `false` = le modifier ne déplacera AUCUN montant.
   */
  applicable: boolean;
  /** Motif de l'inertie, à afficher tel quel (null quand applicable). */
  inapplicableReason: string | null;
}

export interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasMore: boolean;
}

// ─────────────────────────────────────────────────────────────
// Libellés d'affichage (français — homologation DGI)
// ─────────────────────────────────────────────────────────────

export const CONTRACT_TYPE_LABELS: Readonly<Record<ContractType, string>> =
  ACCOUNTING_CONTRACT_TYPE_LABELS;

export const CONTRACT_TYPE_OPTIONS: { value: ContractType; label: string }[] = CONTRACT_TYPES.map(
  (value) => ({ value, label: CONTRACT_TYPE_OPTION_LABELS[value] })
);

/** Options « catégorie professionnelle » (CGI art. 111) — aide au formulaire. */
export const PROFESSIONAL_CATEGORY_OPTIONS: {
  value: ProfessionalCategory;
  label: string;
}[] = [
  { value: 'STANDARD', label: 'Standard — abattement de 25 % pour frais professionnels' },
  {
    value: 'SUPERIEURE',
    label: 'Catégorie supérieure (P / A / B) — abattement réduit à 20 %',
  },
];

/** Options « nature de la prime » (CGI art. 106) — aide au formulaire. */
export const ALLOWANCE_NATURE_OPTIONS: { value: AllowanceNature; label: string }[] = [
  { value: 'LOGEMENT', label: 'Logement — exonéré jusqu’à 75 000 FCFA/mois (art. 106)' },
  { value: 'FONCTION', label: 'Fonction — exonéré jusqu’à 50 000 FCFA/mois (art. 106)' },
  {
    value: 'TRANSPORT',
    label: 'Transport / déplacement — exonéré jusqu’à 30 000 FCFA/mois (art. 106)',
  },
  { value: 'AUTRE', label: 'Autre — aucune exonération dédiée' },
];

export const LEAVE_TYPE_LABELS: Record<LeaveType, string> = {
  CONGE_PAYE: 'Congé payé',
  MALADIE: 'Maladie',
  MATERNITE: 'Maternité',
  SANS_SOLDE: 'Sans solde',
  AUTRE: 'Autre',
};

export const LEAVE_STATUS_LABELS: Record<LeaveStatus, string> = {
  PENDING: 'En attente',
  APPROVED: 'Approuvé',
  REJECTED: 'Rejeté',
};

export const PAYROLL_RUN_STATUS_LABELS: Record<PayrollRunStatus, string> = {
  DRAFT: 'Brouillon',
  VALIDATED: 'Validé',
  PAID: 'Payé',
  IMPORTED: 'Importé',
};

/** Mois en français (index 0 = janvier) — affichage des périodes de paie. */
export const MONTH_LABELS_FR = [
  'Janvier',
  'Février',
  'Mars',
  'Avril',
  'Mai',
  'Juin',
  'Juillet',
  'Août',
  'Septembre',
  'Octobre',
  'Novembre',
  'Décembre',
] as const;

/** Libellé de période « Janvier 2026 » (month 1–12). */
export function payrollPeriodLabel(year: number, month: number): string {
  return `${MONTH_LABELS_FR[month - 1] ?? String(month)} ${year}`;
}

/**
 * Nature d'un paramètre de paie — pilote l'AFFICHAGE et le formulaire
 * d'édition.
 *
 * ★ POURQUOI CE VOCABULAIRE S'EST ÉLARGI (audit paie 2026-07-29). Il ne
 * connaissait que des NOMBRES : `rate`, `amount`, `days`, plus le barème
 * `brackets`. Or `DEFAULT_PAYROLL_PARAMETERS` sème AUSSI quatre booléens
 * (dont `IUTS_ASSIETTE_EXACTE`, qui choisit le mode d'assiette et donc
 * le montant RETENU sur chaque bulletin), un tableau de taux
 * (`IUTS_CHARGE_REDUCTIONS`, art. 113) et deux règles d'arrondi
 * `{ step, mode }`. Faute de nature, tous retombaient sur `amount` et
 * s'affichaient « 0 FCFA » : `true` et `false` devenaient
 * INDISCERNABLES, et un suffixe monétaire pendait à des valeurs qui ne
 * sont pas des montants.
 *
 * - `boolean`    : drapeau — deux libellés explicites, jamais « 0 FCFA » ;
 * - `reductions` : 4 taux indexés par nombre de charges (index 0 = 1
 *                  charge) — le moteur EXIGE exactement 4 taux (art. 113) ;
 * - `rounding`   : règle `{ step, mode }` — LECTURE SEULE (voir plus bas) ;
 * - `unknown`    : code non encore décrit ici — valeur brute, lecture
 *                  seule. Repli HONNÊTE : un code semé demain ne
 *                  réapparaîtra pas en « 0 FCFA ».
 */
export type PayrollParameterKind =
  | 'rate'
  | 'amount'
  | 'brackets'
  | 'days'
  | 'boolean'
  | 'reductions'
  | 'rounding'
  | 'cnssBranches'
  | 'unknown';

export interface PayrollParameterMeta {
  code: string;
  label: string;
  description: string;
  kind: PayrollParameterKind;
  /** true = valeur null acceptée (ex. CNSS_CEILING sans plafond). */
  nullable: boolean;
  /**
   * Libellé de la valeur `true` (kind `boolean`) — « Oui » ne dirait
   * pas quel réglage est en vigueur ; ces drapeaux nomment des MODES.
   */
  trueLabel?: string;
  /** Libellé de la valeur `false` (kind `boolean`). */
  falseLabel?: string;
  /**
   * Motif de la LECTURE SEULE, affiché tel quel. Renseigné pour les
   * natures que l'écran ne sait pas éditer sans risque : mieux vaut le
   * dire que proposer un éditeur qui produit une valeur cassée.
   */
  readOnlyReason?: string;
}

/** Cette nature s'édite-t-elle depuis l'écran ? */
export function isPayrollParameterEditable(meta: PayrollParameterMeta): boolean {
  return meta.readOnlyReason === undefined;
}

/**
 * Motif commun des deux règles d'arrondi — LECTURE SEULE assumée.
 *
 * `{ step, mode }` n'est pas un nombre à saisir : c'est un pas ET une
 * énumération (`down` | `nearest`) qui, mal appariés, déplacent
 * SILENCIEUSEMENT l'assiette et l'impôt de chaque bulletin, sans qu'aucun
 * message ne le signale — le moteur accepte toute paire bien formée. Et
 * la valeur en vigueur relève d'une PRATIQUE comptable constatée, pas
 * d'une prescription du CGI : l'opérateur n'a aucun texte sur lequel
 * s'appuyer pour la changer depuis cet écran. On l'affiche donc en
 * toutes lettres, avec sa provenance, et on n'ouvre pas d'éditeur.
 */
export const PAYROLL_ROUNDING_READ_ONLY_REASON =
  'Lecture seule sur cet écran : une règle d’arrondi associe un pas et un ' +
  'sens (inférieur / au plus proche) qui déplacent silencieusement l’assiette ' +
  'et l’impôt de chaque bulletin. Sa modification passe par votre éditeur, ' +
  'sur demande écrite de votre conseil.';

/**
 * Métadonnées d'affichage des paramètres de paie connus (codes seedés
 * côté serveur — DEFAULT_PAYROLL_PARAMETERS de @efacture/accounting).
 * AUCUNE valeur légale ici : uniquement des libellés.
 */
export const PAYROLL_PARAMETER_META: PayrollParameterMeta[] = [
  {
    code: 'CNSS_EMPLOYEE_RATE',
    label: 'CNSS — part salariale',
    description: 'Taux de cotisation CNSS retenu sur le salaire (assiette plafonnée).',
    kind: 'rate',
    nullable: false,
  },
  {
    code: 'CNSS_EMPLOYER_RATE',
    label: 'CNSS — part patronale',
    description: 'Taux de cotisation CNSS à la charge de l’employeur (même assiette).',
    kind: 'rate',
    nullable: false,
  },
  {
    code: 'CNSS_CEILING',
    label: 'Plafond CNSS mensuel',
    description: 'Plafond mensuel de l’assiette CNSS en FCFA (vide = pas de plafond).',
    kind: 'amount',
    nullable: true,
  },
  {
    code: 'CNSS_PF_EMPLOYER_RATE',
    label: 'CNSS — branche des prestations familiales (part patronale)',
    description:
      'Part du taux patronal CNSS affectée aux prestations familiales (décret ' +
      'n°2023-0129, art. 2). Elle n’est pas due pour un contrat qui ne cotise pas ' +
      'à cette branche (stage).',
    kind: 'rate',
    nullable: false,
  },
  {
    code: 'CNSS_RP_EMPLOYER_RATE',
    label: 'CNSS — branche des risques professionnels (part patronale)',
    description:
      'Part du taux patronal CNSS affectée aux risques professionnels (décret ' +
      'n°2023-0129, art. 2).',
    kind: 'rate',
    nullable: false,
  },
  {
    code: 'CNSS_PENSIONS_EMPLOYER_RATE',
    label: 'CNSS — branche des pensions (part patronale)',
    description:
      'Part du taux patronal CNSS affectée aux pensions (décret n°2023-0129, ' +
      'art. 2) ; la part salariale relève de la même branche (art. 3).',
    kind: 'rate',
    nullable: false,
  },
  {
    code: 'CNSS_BRANCHES_BY_CONTRACT',
    label: 'CNSS — branches par type de contrat',
    description:
      'Branches CNSS auxquelles cotise chaque type de contrat (formulaires CNSS ' +
      'Pr-RCS/Cot 04 et Cot 08) : le stagiaire ou l’apprenti cotise aux risques ' +
      'professionnels et aux pensions, pas aux prestations familiales.',
    kind: 'cnssBranches',
    nullable: false,
    readOnlyReason:
      'Lecture seule sur cet écran : la répartition des branches par type de ' +
      'contrat suit les définitions des formulaires CNSS. Sa modification passe ' +
      'par votre éditeur, sur demande écrite de votre conseil.',
  },
  {
    code: 'CNSS_ASSIETTE_INCLUT_NON_IMPOSABLES',
    label: 'Assiette CNSS — éléments non imposables non qualifiés',
    description:
      'Sort des primes et indemnités NON imposables à l’IUTS qui ne sont pas ' +
      'qualifiées de remboursement de frais : entrent-elles dans l’assiette CNSS ?',
    kind: 'boolean',
    nullable: false,
    trueLabel: 'Cotisables (assiette large)',
    falseLabel: 'Hors assiette (lecture historique)',
  },
  {
    code: 'IUTS_ABATTEMENT_RATE',
    label: 'Abattement IUTS',
    description: 'Abattement appliqué à l’assiette IUTS après CNSS (frais professionnels).',
    kind: 'rate',
    nullable: false,
  },
  {
    code: 'IUTS_BRACKETS',
    label: 'Barème IUTS',
    description:
      'Barème progressif mensuel de l’IUTS par tranches — la dernière tranche est illimitée.',
    kind: 'brackets',
    nullable: false,
  },
  {
    code: 'TPA_RATE',
    label: 'TPA — taxe patronale d’apprentissage',
    description: 'Taux de la TPA, charge patronale assise sur le brut total.',
    kind: 'rate',
    nullable: false,
  },
  {
    code: 'TPA_ASSIETTE_INCLUT_REMBOURSEMENTS',
    label: 'Assiette TPA — remboursements de frais',
    description:
      'Les frais ayant un caractère de remboursement entrent-ils dans l’assiette ' +
      'de la TPA ? (Cas non tranché par le texte — interprétation documentée.)',
    kind: 'boolean',
    nullable: false,
    trueLabel: 'Inclus dans l’assiette',
    falseLabel: 'Exclus de l’assiette',
  },
  {
    code: 'FSP_RATE',
    label: 'FSP — Fonds de soutien patriotique',
    description:
      'Taux du prélèvement FSP retenu sur le salaire net (décret n°2024-0027) ' +
      '— reversé dans les mêmes délais que l’IUTS.',
    kind: 'rate',
    nullable: false,
  },
  {
    code: 'FSP_ROUNDING',
    label: 'FSP — arrondi du prélèvement',
    description:
      'Règle d’arrondi appliquée au FSP retenu (le décret n°2024-0027 n’en fixe ' +
      'aucune ; par défaut, comme l’IUTS à payer du classeur de paie du conseil ' +
      'comptable).',
    kind: 'rounding',
    nullable: false,
    readOnlyReason: PAYROLL_ROUNDING_READ_ONLY_REASON,
  },
  {
    code: 'FSP_ASSIETTE_INCLUT_REMBOURSEMENTS',
    label: 'Assiette FSP — remboursements de frais',
    description:
      'Les frais ayant un caractère de remboursement entrent-ils dans la ' +
      'rémunération brute dont dérive le salaire net soumis au FSP ? ' +
      '(Cas non tranché par le texte — interprétation documentée.)',
    kind: 'boolean',
    nullable: false,
    trueLabel: 'Inclus dans l’assiette',
    falseLabel: 'Exclus de l’assiette',
  },
  {
    code: 'AMU_EMPLOYEE_RATE',
    label: 'AMU — part salariale',
    description:
      'Taux de cotisation à l’assurance maladie universelle retenu sur le ' +
      'salarié (décret n°2024-0345). Désactivé par défaut tant que la ' +
      'collecte effective du secteur privé n’est pas confirmée.',
    kind: 'rate',
    nullable: false,
  },
  {
    code: 'AMU_EMPLOYER_RATE',
    label: 'AMU — part patronale',
    description:
      'Taux de cotisation à l’assurance maladie universelle à la charge de ' +
      'l’employeur (décret n°2024-0345). Désactivé par défaut tant que la ' +
      'collecte effective du secteur privé n’est pas confirmée.',
    kind: 'rate',
    nullable: false,
  },
  // ── Mode d'assiette IUTS exacte (CGI arts. 106/111/113) ──────────
  // ★ Ces codes GOUVERNENT tous les bulletins (le mode exact est le
  // défaut). Sans libellé, ils retombaient sur `kind: 'amount'` et un
  // taux de 0,25 s'affichait « 0 FCFA » : le paramètre qui applique
  // l'art. 111 était le moins lisible de l'écran, et le renvoi du badge
  // « c'est IUTS_ABATTEMENT_BASE_RATE qui gouverne » pointait vers lui.
  // AUCUNE valeur légale ici : uniquement des libellés.
  {
    // ★ LE DRAPEAU DE MODE — il choisit la BRANCHE d'assiette IUTS que
    // le moteur exécute, donc le montant RETENU sur chaque bulletin.
    // C'est lui que le repli `amount` affichait « 0 FCFA », à
    // l'identique pour `true` et pour `false`.
    code: 'IUTS_ASSIETTE_EXACTE',
    label: 'Mode d’assiette IUTS',
    description:
      'Mode de calcul de l’assiette IUTS : exact (exonérations d’indemnités par ' +
      'nature, abattement sur le salaire de base, déduction retraite plafonnée, ' +
      'réductions pour charges de famille — CGI arts. 106/111/113) ou simple ' +
      '(abattement unique sur l’assiette après CNSS). Basculer ce mode change ' +
      'les montants retenus sur les bulletins.',
    kind: 'boolean',
    nullable: false,
    trueLabel: 'Mode exact (CGI arts. 106/111/113)',
    falseLabel: 'Mode simple (abattement unique)',
  },
  {
    code: 'IUTS_CNSS_CAP_BASE_RATE',
    label: 'IUTS — plafond des retenues retraite déductibles',
    description:
      'Part du salaire de base au-delà de laquelle les retenues retraite ne sont plus déductibles de l’assiette IUTS (CGI art. 111).',
    kind: 'rate',
    nullable: false,
  },
  {
    code: 'IUTS_ABATTEMENT_BASE_RATE',
    label: 'IUTS — abattement pour frais professionnels',
    description:
      'Abattement forfaitaire pour frais et charges professionnels, appliqué au SALAIRE DE BASE (CGI art. 111). C’est ce paramètre qui gouverne l’abattement en mode d’assiette exacte.',
    kind: 'rate',
    nullable: false,
  },
  {
    code: 'IUTS_ABATTEMENT_BASE_RATE_SUPERIEURE',
    label: 'IUTS — abattement, catégories supérieures',
    description:
      'Abattement réduit applicable aux catégories supérieures (« P », « A », « B », ou 6, 1 et 2 du secteur public et grilles équivalentes) — CGI art. 111.',
    kind: 'rate',
    nullable: false,
  },
  {
    code: 'IUTS_EXO_LOGEMENT_RATE',
    label: 'IUTS — exonération logement (taux)',
    description:
      'Part du salaire brut fiscal exonérée au titre de l’indemnité de logement (CGI art. 106), dans la limite du plafond ci-dessous.',
    kind: 'rate',
    nullable: false,
  },
  {
    code: 'IUTS_EXO_LOGEMENT_CAP',
    label: 'IUTS — exonération logement (plafond)',
    description:
      'Plafond mensuel de l’exonération de l’indemnité de logement (CGI art. 106).',
    kind: 'amount',
    nullable: false,
  },
  {
    code: 'IUTS_EXO_TRANSPORT_RATE',
    label: 'IUTS — exonération transport (taux)',
    description:
      'Part du salaire brut fiscal exonérée au titre de l’indemnité de transport ou de déplacement (CGI art. 106).',
    kind: 'rate',
    nullable: false,
  },
  {
    code: 'IUTS_EXO_TRANSPORT_CAP',
    label: 'IUTS — exonération transport (plafond)',
    description:
      'Plafond mensuel de l’exonération de l’indemnité de transport ou de déplacement (CGI art. 106).',
    kind: 'amount',
    nullable: false,
  },
  {
    code: 'IUTS_EXO_FONCTION_RATE',
    label: 'IUTS — exonération de fonction (taux)',
    description:
      'Part du salaire brut fiscal exonérée au titre de l’indemnité de fonction (CGI art. 106).',
    kind: 'rate',
    nullable: false,
  },
  {
    code: 'IUTS_EXO_FONCTION_CAP',
    label: 'IUTS — exonération de fonction (plafond)',
    description:
      'Plafond mensuel de l’exonération de l’indemnité de fonction (CGI art. 106).',
    kind: 'amount',
    nullable: false,
  },
  {
    // 4 taux, indexés par nombre de charges (index 0 = 1 charge). Ce
    // sont des TAUX (réduction appliquée à l'impôt), pas des montants :
    // le repli les affichait « 0 FCFA », soit un barème entier réduit à
    // un montant unique — et un montant faux.
    code: 'IUTS_CHARGE_REDUCTIONS',
    label: 'IUTS — réductions pour charges de famille',
    description:
      'Réductions appliquées à l’IMPÔT selon le nombre de charges de famille ' +
      '(1 à 4 charges, CGI art. 113). Le moteur de paie exige exactement quatre taux.',
    kind: 'reductions',
    nullable: false,
  },
  {
    code: 'IUTS_BASE_ROUNDING',
    label: 'IUTS — arrondi de l’assiette',
    description:
      'Règle d’arrondi appliquée à l’assiette IUTS avant le barème (pratique ' +
      'comptable constatée, pas une prescription du CGI).',
    kind: 'rounding',
    nullable: false,
    readOnlyReason: PAYROLL_ROUNDING_READ_ONLY_REASON,
  },
  {
    code: 'IUTS_PAYABLE_ROUNDING',
    label: 'IUTS — arrondi de l’impôt à payer',
    description:
      'Règle d’arrondi appliquée à l’IUTS après réduction pour charges de famille ' +
      '(pratique comptable constatée, pas une prescription du CGI).',
    kind: 'rounding',
    nullable: false,
    readOnlyReason: PAYROLL_ROUNDING_READ_ONLY_REASON,
  },
  {
    code: 'CONGES_PAYES_JOURS_ANNUELS',
    label: 'Congés payés — droit annuel',
    description:
      'Droit annuel de congés payés en jours ouvrés (module RH — solde de congés). N’intervient pas dans le calcul du bulletin.',
    kind: 'days',
    nullable: false,
  },
];

/**
 * Métadonnées d'un code — avec un REPLI HONNÊTE pour un code non
 * encore décrit ici.
 *
 * ★ LE REPLI ÉTAIT `kind: 'amount'` : c'est LUI qui affichait « 0 FCFA »
 * pour les sept codes sans nature, et il l'aurait refait au premier code
 * semé côté serveur sans passage par ce fichier. Un repli qui INVENTE
 * une nature ment ; celui-ci dit ce qu'il sait — la valeur brute, en
 * lecture seule, à décrire ici avant de pouvoir la modifier.
 */
export function payrollParameterMeta(code: string): PayrollParameterMeta {
  return (
    PAYROLL_PARAMETER_META.find((meta) => meta.code === code) ?? {
      code,
      label: code,
      description:
        'Paramètre non décrit dans cette version de l’interface : sa valeur est ' +
        'affichée telle qu’elle est enregistrée.',
      kind: 'unknown',
      nullable: true,
      readOnlyReason:
        'Lecture seule : cette version de l’interface ne connaît pas la nature de ' +
        'ce paramètre et ne peut donc pas garantir la forme d’une valeur saisie. ' +
        'Mettez à jour l’application pour le modifier.',
    }
  );
}

// ─────────────────────────────────────────────────────────────
// Aides
// ─────────────────────────────────────────────────────────────

/** Les colonnes Decimal Prisma sont sérialisées en string en JSON. */
export { toNum } from '@efacture/web-billing/lib/decimal';

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Jours ouvrés lundi→vendredi, bornes INCLUSES, en UTC — MIROIR du
 * calcul serveur (leave.service countBusinessDays) pour l'affichage
 * en direct dans le formulaire de demande. Le serveur reste la
 * référence : il recalcule toujours à la création.
 */
export function countBusinessDays(startISO: string, endISO: string): number {
  const start = new Date(`${startISO}T00:00:00Z`);
  const end = new Date(`${endISO}T00:00:00Z`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 0;
  if (start.getTime() > end.getTime()) return 0;
  let count = 0;
  for (let t = start.getTime(); t <= end.getTime(); t += DAY_MS) {
    const day = new Date(t).getUTCDay(); // 0 = dimanche … 6 = samedi
    if (day >= 1 && day <= 5) count += 1;
  }
  return count;
}

// ─────────────────────────────────────────────────────────────
// Paramètres et corps de requêtes
// ─────────────────────────────────────────────────────────────

export interface EmployeeListParams {
  search?: string | undefined;
  department?: string | undefined;
  contractType?: ContractType | undefined;
  isActive?: boolean | undefined;
  page?: number | undefined;
  limit?: number | undefined;
}

export interface EmployeeInput {
  firstName: string;
  lastName: string;
  position: string;
  /** Required at creation; pay fields are sent only with payroll:run (#533). */
  baseSalary?: number;
  hireDate: string; // YYYY-MM-DD
  endDate?: string | null;
  contractType?: ContractType;
  email?: string | null;
  phone?: string | null;
  department?: string | null;
  cnssNumber?: string | null;
  ifu?: string | null;
  paymentMethod?: 'VIREMENT' | 'CHEQUE' | 'ESPECES' | 'MOBILE_MONEY';
  bankName?: string | null;
  bankAccount?: string | null;
  allowances?: EmployeeAllowance[] | null;
  /** Charges de famille (CGI art. 113) — entier 0 à 4. */
  familyCharges?: number;
  /** Catégorie professionnelle (CGI art. 111) — STANDARD | SUPERIEURE. */
  professionalCategory?: ProfessionalCategory;
  /** CGI art. 117, 1°; null clears it. */
  address?: string | null;
  /** CGI art. 117, 4°: integer ≥ 0, null when not entered. */
  dependentChildren?: number | null;
  costCenterId?: string | null;
  userId?: string | null;
  isActive?: boolean;
}

export interface DeleteEmployeeResult {
  /** true = suppression réelle (aucun bulletin de paie). */
  deleted: boolean;
  /** true = bulletins existants → désactivation à la place. */
  deactivated: boolean;
}

export interface LeaveListParams {
  status?: LeaveStatus | undefined;
  employeeId?: string | undefined;
  type?: LeaveType | undefined;
  page?: number | undefined;
  limit?: number | undefined;
}

export interface LeaveInput {
  employeeId: string;
  type: LeaveType;
  startDate: string; // YYYY-MM-DD
  endDate: string; // YYYY-MM-DD
  reason?: string | null;
}

export interface PayrollRunListParams {
  year?: number | undefined;
  status?: PayrollRunStatus | undefined;
  page?: number | undefined;
  limit?: number | undefined;
}

export interface UpdatePayrollSlipInput {
  oneOffBonuses?: PayslipOneOffBonus[];
  oneOffDeductions?: PayslipOneOffDeduction[];
}

export interface SetPayrollParameterInput {
  code: string;
  value: unknown;
  effectiveFrom: string; // YYYY-MM-DD
  /** Absente = valeur vérifiée par le tenant (la note est effacée). */
  note?: string | null | undefined;
  /** Référence légale du texte source (optionnelle, max 300 côté serveur). */
  legalReference?: string | null | undefined;
  /**
   * ★ ACQUITTEMENT d'une valeur qui S'ÉCARTE du référentiel sourcé.
   * Sans lui, le serveur refuse un tel écart (409
   * PARAMETRE_PAIE_INHABITUEL) ; avec lui ET son motif, il l'enregistre
   * et le trace. La COMPARAISON au référentiel n'est PAS refaite ici :
   * elle vit dans @efacture/accounting et n'a qu'un seul juge.
   */
  acknowledgeUnusualValue?: boolean | undefined;
  /** Motif ÉCRIT, obligatoire avec l'acquittement (max 500 côté serveur). */
  reason?: string | null | undefined;
}

/** Résultat de l'application des barèmes officiels (codes par catégorie). */
export interface ApplyOfficialDefaultsResult {
  /** Codes mis à jour (valeur + référence légale du registre fiscal). */
  applied: string[];
  /** Codes ignorés (valeur vérifiée/annotée par le tenant — jamais écrasée). */
  skipped: string[];
  /**
   * ★ Écarts DÉTECTÉS que ce chemin refuse de déplacer (constat F6) :
   * leur changement modifierait les montants retenus sur les bulletins.
   * La décision revient à l'utilisateur, par une valeur datée.
   */
  requiresDecision: Array<{
    code: string;
    inForceValue: unknown;
    referenceValue: unknown;
    reason: string;
  }>;
}

// ─────────────────────────────────────────────────────────────
// Réponses (tableaux nommés contextuellement — pitfall CLAUDE.md)
// ─────────────────────────────────────────────────────────────

export interface EmployeeListResponse {
  employees: Employee[];
  pagination: PaginationInfo;
}

export interface LeaveListResponse {
  leaves: LeaveRequest[];
  pagination: PaginationInfo;
}

export interface PayrollRunListResponse {
  payrollRuns: PayrollRun[];
  pagination: PaginationInfo;
}

export interface PayrollParametersResponse {
  parameters: ResolvedPayrollParameter[];
  /** true si AU MOINS un paramètre porte la note « à vérifier ». */
  aVerifier: boolean;
  year: number;
  month: number;
}

export interface UpdatePayrollSlipResponse {
  /** Bulletin recalculé par le moteur (sans l'employé inclus). */
  payslip: Omit<PayrollSlip, 'employee'>;
  payrollRun: PayrollRun;
}

export interface ValidatePayrollRunResponse {
  payrollRun: PayrollRunDetail;
  /** Écriture comptable générée (sourceId PAIE-{année}-{MM}). */
  entry: { id: string; entryNumber: string | null; label: string };
}

// ─────────────────────────────────────────────────────────────
// Query Keys
// ─────────────────────────────────────────────────────────────

export const hrKeys = {
  all: ['hr'] as const,
  employees: () => [...hrKeys.all, 'employees'] as const,
  employeeList: (params: EmployeeListParams) =>
    [...hrKeys.employees(), 'list', params] as const,
  employeeDetail: (id: string) => [...hrKeys.employees(), 'detail', id] as const,
  leaves: () => [...hrKeys.all, 'leaves'] as const,
  leaveList: (params: LeaveListParams) => [...hrKeys.leaves(), 'list', params] as const,
  leaveBalance: (employeeId: string, year?: number) =>
    [...hrKeys.leaves(), 'balance', employeeId, year ?? 'current'] as const,
  payroll: () => [...hrKeys.all, 'payroll'] as const,
  runList: (params: PayrollRunListParams) =>
    [...hrKeys.payroll(), 'runs', 'list', params] as const,
  runDetail: (id: string) => [...hrKeys.payroll(), 'runs', 'detail', id] as const,
  /** Visa history (hr-visa) — { events } from GET .../runs/:id/visas. */
  runVisas: (id: string) => [...hrKeys.payroll(), 'runs', 'visas', id] as const,
  parameters: (year?: number, month?: number) =>
    [...hrKeys.payroll(), 'parameters', year ?? 'current', month ?? 'current'] as const,
};

// ─────────────────────────────────────────────────────────────
// Fonctions API — employés
// ─────────────────────────────────────────────────────────────

async function fetchEmployees(params: EmployeeListParams): Promise<EmployeeListResponse> {
  return apiClient.get<EmployeeListResponse>('/hr/employees', {
    search: params.search || undefined,
    department: params.department || undefined,
    contractType: params.contractType,
    // boolean → 'true'/'false' dans la query string (schéma serveur)
    isActive: params.isActive === undefined ? undefined : params.isActive,
    page: params.page,
    limit: params.limit,
  });
}

async function fetchEmployee(id: string): Promise<Employee> {
  const result = await apiClient.get<{ employee: Employee }>(`/hr/employees/${id}`);
  return result.employee;
}

async function createEmployee(data: EmployeeInput): Promise<Employee> {
  const result = await apiClient.post<{ employee: Employee }>('/hr/employees', data);
  return result.employee;
}

async function updateEmployee(
  id: string,
  data: Partial<EmployeeInput>
): Promise<Employee> {
  const result = await apiClient.put<{ employee: Employee }>(
    `/hr/employees/${id}`,
    data
  );
  return result.employee;
}

async function deleteEmployee(id: string): Promise<DeleteEmployeeResult> {
  return apiClient.delete<DeleteEmployeeResult>(`/hr/employees/${id}`);
}

// ─────────────────────────────────────────────────────────────
// Fonctions API — congés
// ─────────────────────────────────────────────────────────────

async function fetchLeaves(params: LeaveListParams): Promise<LeaveListResponse> {
  return apiClient.get<LeaveListResponse>('/hr/leaves', {
    status: params.status,
    employeeId: params.employeeId,
    type: params.type,
    page: params.page,
    limit: params.limit,
  });
}

async function fetchLeaveBalance(
  employeeId: string,
  year?: number
): Promise<LeaveBalance> {
  const result = await apiClient.get<{ balance: LeaveBalance }>(
    `/hr/leaves/balance/${employeeId}`,
    { year }
  );
  return result.balance;
}

async function createLeave(data: LeaveInput): Promise<LeaveRequest> {
  const result = await apiClient.post<{ leave: LeaveRequest }>('/hr/leaves', data);
  return result.leave;
}

/** POST sans corps — l'apiClient n'envoie pas de Content-Type sans body. */
async function approveLeave(id: string): Promise<LeaveRequest> {
  const result = await apiClient.post<{ leave: LeaveRequest }>(
    `/hr/leaves/${id}/approve`
  );
  return result.leave;
}

async function rejectLeave(id: string): Promise<LeaveRequest> {
  const result = await apiClient.post<{ leave: LeaveRequest }>(
    `/hr/leaves/${id}/reject`
  );
  return result.leave;
}

// ─────────────────────────────────────────────────────────────
// Fonctions API — paie
// ─────────────────────────────────────────────────────────────

async function fetchPayrollRuns(
  params: PayrollRunListParams
): Promise<PayrollRunListResponse> {
  return apiClient.get<PayrollRunListResponse>('/hr/payroll/runs', {
    year: params.year,
    status: params.status,
    page: params.page,
    limit: params.limit,
  });
}

async function fetchPayrollRun(id: string): Promise<PayrollRunDetail> {
  const result = await apiClient.get<{ payrollRun: PayrollRunDetail }>(
    `/hr/payroll/runs/${id}`
  );
  return result.payrollRun;
}

async function createPayrollRun(year: number, month: number): Promise<PayrollRunDetail> {
  const result = await apiClient.post<{ payrollRun: PayrollRunDetail }>(
    '/hr/payroll/runs',
    { year, month }
  );
  return result.payrollRun;
}

async function deletePayrollRun(id: string): Promise<void> {
  await apiClient.delete(`/hr/payroll/runs/${id}`);
}

async function updatePayrollSlip(
  runId: string,
  slipId: string,
  data: UpdatePayrollSlipInput
): Promise<UpdatePayrollSlipResponse> {
  return apiClient.put<UpdatePayrollSlipResponse>(
    `/hr/payroll/runs/${runId}/slips/${slipId}`,
    data
  );
}

async function regeneratePayrollRun(id: string): Promise<PayrollRunDetail> {
  const result = await apiClient.post<{ payrollRun: PayrollRunDetail }>(
    `/hr/payroll/runs/${id}/regenerate`
  );
  return result.payrollRun;
}

async function validatePayrollRun(id: string): Promise<ValidatePayrollRunResponse> {
  return apiClient.post<ValidatePayrollRunResponse>(`/hr/payroll/runs/${id}/validate`);
}

/** Visas the run (accountant countersignature, hr-visa). POST .../runs/:id/visa */
async function visaPayrollRun(id: string): Promise<PayrollRunEvent> {
  const result = await apiClient.post<{ event: PayrollRunEvent }>(
    `/hr/payroll/runs/${id}/visa`
  );
  return result.event;
}

/**
 * Refuses the visa — written reason mandatory (hr-visa). Does NOT
 * change the run's status: it stays DRAFT. POST .../runs/:id/refus-de-visa
 */
async function refuseVisaPayrollRun(id: string, reason: string): Promise<PayrollRunEvent> {
  const result = await apiClient.post<{ event: PayrollRunEvent }>(
    `/hr/payroll/runs/${id}/refus-de-visa`,
    { reason }
  );
  return result.event;
}

/** Visa history — for the auditor. GET .../runs/:id/visas */
async function fetchPayrollRunVisas(id: string): Promise<PayrollRunEvent[]> {
  const result = await apiClient.get<{ events: PayrollRunEvent[] }>(
    `/hr/payroll/runs/${id}/visas`
  );
  return result.events;
}

async function payPayrollRun(
  id: string,
  date?: string
): Promise<ValidatePayrollRunResponse> {
  return apiClient.post<ValidatePayrollRunResponse>(
    `/hr/payroll/runs/${id}/pay`,
    date ? { date } : {}
  );
}

async function fetchPayrollParameters(
  year?: number,
  month?: number
): Promise<PayrollParametersResponse> {
  return apiClient.get<PayrollParametersResponse>('/hr/payroll/parameters', {
    year,
    month,
  });
}

async function setPayrollParameter(
  data: SetPayrollParameterInput
): Promise<ResolvedPayrollParameter> {
  const result = await apiClient.put<{ parameter: ResolvedPayrollParameter }>(
    '/hr/payroll/parameters',
    data
  );
  return result.parameter;
}

async function applyOfficialPayrollDefaults(): Promise<ApplyOfficialDefaultsResult> {
  return apiClient.post<ApplyOfficialDefaultsResult>(
    '/hr/payroll/parameters/apply-official-defaults'
  );
}

/**
 * Télécharge le bulletin de paie individuel en PDF.
 * GET /api/hr/payroll/runs/:id/slips/:slipId/pdf
 */
export function downloadPayslipPdf(
  runId: string,
  slipId: string,
  filename: string
): Promise<void> {
  return apiClient.download(`/hr/payroll/runs/${runId}/slips/${slipId}/pdf`, filename);
}

/**
 * Télécharge l'état de centralisation de la paie (aperçu imprimable de
 * l'écriture OD — un employé par ligne, obligations CNSS/IUTS/TPA en
 * pied). Disponible à n'importe quel statut du cycle (DRAFT, VALIDATED,
 * PAID) — jamais restreint au brouillon.
 * GET /api/hr/payroll/runs/:id/centralisation/pdf
 */
export function downloadPayrollCentralisationPdf(
  runId: string,
  filename: string
): Promise<void> {
  return apiClient.download(`/hr/payroll/runs/${runId}/centralisation/pdf`, filename);
}

/** One job held, for the certificat de travail (dates YYYY-MM-DD). */
export interface CertificatTravailEmploi {
  nature: string;
  du: string;
  au: string;
}

/**
 * Downloads the certificat de travail of an employee whose contract has
 * ended. POST /api/hr/employees/:id/certificat-travail
 */
export function downloadCertificatTravailPdf(
  employeeId: string,
  body: { emplois: CertificatTravailEmploi[]; deliveredAt: string },
  filename: string
): Promise<void> {
  return apiClient.downloadPost(`/hr/employees/${employeeId}/certificat-travail`, body, filename);
}

// ─────────────────────────────────────────────────────────────
// Queries — employés
// ─────────────────────────────────────────────────────────────

/** Liste paginée des employés. GET /api/hr/employees */
export function useEmployees(
  params: EmployeeListParams = {},
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: hrKeys.employeeList(params),
    queryFn: () => fetchEmployees(params),
    enabled: options?.enabled ?? true,
  });
}

/** Détail d'un employé. GET /api/hr/employees/:id */
export function useEmployee(id: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: hrKeys.employeeDetail(id),
    queryFn: () => fetchEmployee(id),
    enabled: (options?.enabled ?? true) && !!id,
  });
}

/** Création — matricule EMP-NNNN par séquence atomique. POST /api/hr/employees */
export function useCreateEmployee() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: EmployeeInput) => createEmployee(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hrKeys.employees() });
    },
  });
}

/** Modification (le matricule est immuable). PUT /api/hr/employees/:id */
export function useUpdateEmployee() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<EmployeeInput> }) =>
      updateEmployee(id, data),
    onSuccess: (employee) => {
      queryClient.setQueryData(hrKeys.employeeDetail(employee.id), employee);
      queryClient.invalidateQueries({ queryKey: hrKeys.employees() });
    },
  });
}

/**
 * Suppression — DÉSACTIVATION si des bulletins de paie existent
 * (le serveur décide : { deleted, deactivated }).
 * DELETE /api/hr/employees/:id
 */
export function useDeleteEmployee() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteEmployee(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hrKeys.employees() });
    },
  });
}

// ─────────────────────────────────────────────────────────────
// Queries — congés
// ─────────────────────────────────────────────────────────────

/** Liste paginée des demandes de congés. GET /api/hr/leaves */
export function useLeaves(
  params: LeaveListParams = {},
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: hrKeys.leaveList(params),
    queryFn: () => fetchLeaves(params),
    enabled: options?.enabled ?? true,
  });
}

/** Solde de congés payés de l'année. GET /api/hr/leaves/balance/:employeeId */
export function useLeaveBalance(
  employeeId: string,
  year?: number,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: hrKeys.leaveBalance(employeeId, year),
    queryFn: () => fetchLeaveBalance(employeeId, year),
    enabled: (options?.enabled ?? true) && !!employeeId,
  });
}

/** Nouvelle demande (PENDING — jours ouvrés calculés côté serveur). POST /api/hr/leaves */
export function useCreateLeave() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: LeaveInput) => createLeave(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hrKeys.leaves() });
    },
  });
}

/** Approbation (hr:approve — décision immuable). POST /api/hr/leaves/:id/approve */
export function useApproveLeave() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => approveLeave(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hrKeys.leaves() });
    },
  });
}

/** Rejet (hr:approve — décision immuable). POST /api/hr/leaves/:id/reject */
export function useRejectLeave() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => rejectLeave(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hrKeys.leaves() });
    },
  });
}

// ─────────────────────────────────────────────────────────────
// Queries — paie
// ─────────────────────────────────────────────────────────────

/** Liste paginée des cycles de paie. GET /api/hr/payroll/runs */
export function usePayrollRuns(
  params: PayrollRunListParams = {},
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: hrKeys.runList(params),
    queryFn: () => fetchPayrollRuns(params),
    enabled: options?.enabled ?? true,
  });
}

/** Détail d'un cycle + bulletins. GET /api/hr/payroll/runs/:id */
export function usePayrollRun(id: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: hrKeys.runDetail(id),
    queryFn: () => fetchPayrollRun(id),
    enabled: (options?.enabled ?? true) && !!id,
  });
}

/**
 * Génère le cycle du mois — un bulletin par employé actif, calculé par
 * le moteur (409 CYCLE_EXISTANT si le mois existe déjà).
 * POST /api/hr/payroll/runs
 */
export function useCreatePayrollRun() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ year, month }: { year: number; month: number }) =>
      createPayrollRun(year, month),
    onSuccess: (run) => {
      queryClient.setQueryData(hrKeys.runDetail(run.id), run);
      queryClient.invalidateQueries({ queryKey: hrKeys.payroll() });
    },
  });
}

/** Suppression d'un BROUILLON (un cycle validé est immuable). DELETE /api/hr/payroll/runs/:id */
export function useDeletePayrollRun() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deletePayrollRun(id),
    onSuccess: (_, deletedId) => {
      queryClient.removeQueries({ queryKey: hrKeys.runDetail(deletedId) });
      queryClient.invalidateQueries({ queryKey: hrKeys.payroll() });
    },
  });
}

/**
 * Primes/retenues ponctuelles d'un bulletin en brouillon — tous les
 * montants sont RECALCULÉS par le moteur.
 * PUT /api/hr/payroll/runs/:id/slips/:slipId
 */
export function useUpdatePayrollSlip() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      runId,
      slipId,
      data,
    }: {
      runId: string;
      slipId: string;
      data: UpdatePayrollSlipInput;
    }) => updatePayrollSlip(runId, slipId, data),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: hrKeys.runDetail(variables.runId) });
      queryClient.invalidateQueries({ queryKey: hrKeys.payroll() });
    },
  });
}

/**
 * Régénère tous les bulletins d'un brouillon (les éléments ponctuels
 * sont réinitialisés). POST /api/hr/payroll/runs/:id/regenerate
 */
export function useRegeneratePayrollRun() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => regeneratePayrollRun(id),
    onSuccess: (run) => {
      queryClient.setQueryData(hrKeys.runDetail(run.id), run);
      queryClient.invalidateQueries({ queryKey: hrKeys.payroll() });
    },
  });
}

/**
 * Validation — DRAFT → VALIDATED (immuable) + UNE écriture comptable
 * OD PAIE-{année}-{MM}. POST /api/hr/payroll/runs/:id/validate
 */
export function useValidatePayrollRun() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => validatePayrollRun(id),
    onSuccess: (result) => {
      queryClient.setQueryData(hrKeys.runDetail(result.payrollRun.id), result.payrollRun);
      queryClient.invalidateQueries({ queryKey: hrKeys.payroll() });
      // L'écriture OD vient d'être créée — les listes d'écritures changent.
      queryClient.invalidateQueries({ queryKey: ['accounting', 'entries'] });
    },
  });
}

/**
 * Visa history (hr-visa) — for the auditor.
 * GET /api/hr/payroll/runs/:id/visas
 */
export function usePayrollRunVisas(id: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: hrKeys.runVisas(id),
    queryFn: () => fetchPayrollRunVisas(id),
    enabled: (options?.enabled ?? true) && !!id,
  });
}

/**
 * Visas the run (accountant countersignature, hr-visa) — writes NO
 * status on the run (stays DRAFT). POST /api/hr/payroll/runs/:id/visa
 */
export function useVisaPayrollRun() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => visaPayrollRun(id),
    onSuccess: (_event, id) => {
      queryClient.invalidateQueries({ queryKey: hrKeys.runVisas(id) });
    },
  });
}

/**
 * Refuses the visa — written reason mandatory. The run stays DRAFT.
 * POST /api/hr/payroll/runs/:id/refus-de-visa
 */
export function useRefuseVisaPayrollRun() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      refuseVisaPayrollRun(id, reason),
    onSuccess: (_event, variables) => {
      queryClient.invalidateQueries({ queryKey: hrKeys.runVisas(variables.id) });
    },
  });
}

/**
 * Règlement du net (écriture BQ D 422 / C 521, PAIEREG-{année}-{MM})
 * → statut PAID. POST /api/hr/payroll/runs/:id/pay
 */
export function usePayPayrollRun() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, date }: { id: string; date?: string | undefined }) => payPayrollRun(id, date),
    onSuccess: (result) => {
      queryClient.setQueryData(hrKeys.runDetail(result.payrollRun.id), result.payrollRun);
      queryClient.invalidateQueries({ queryKey: hrKeys.payroll() });
      queryClient.invalidateQueries({ queryKey: ['accounting', 'entries'] });
    },
  });
}

/**
 * Paramètres de paie applicables à une période (défaut : mois courant).
 * Les défauts seedés portent la note « à vérifier avec votre conseil »
 * (aVerifier) — le bandeau des pages paie l'affiche.
 * GET /api/hr/payroll/parameters
 */
export function usePayrollParameters(
  year?: number,
  month?: number,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: hrKeys.parameters(year, month),
    queryFn: () => fetchPayrollParameters(year, month),
    staleTime: 1000 * 60,
    enabled: options?.enabled ?? true,
  });
}

/**
 * Valeur datée d'un paramètre (payroll:validate) — la forme est
 * validée par le MOTEUR réel côté serveur. Sans note fournie, la note
 * « à vérifier » est effacée. PUT /api/hr/payroll/parameters
 */
export function useSetPayrollParameter() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: SetPayrollParameterInput) => setPayrollParameter(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hrKeys.payroll() });
    },
  });
}

/**
 * Applique les barèmes officiels (valeur + référence légale du registre
 * fiscal P0.2) en lignes datées d'aujourd'hui, UNIQUEMENT pour les
 * paramètres jamais vérifiés par le tenant (une valeur validée n'est
 * JAMAIS écrasée). payroll:validate.
 * POST /api/hr/payroll/parameters/apply-official-defaults
 */
export function useApplyOfficialPayrollDefaults() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => applyOfficialPayrollDefaults(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hrKeys.payroll() });
    },
  });
}

// ─────────────────────────────────────────────────────────────
// Exports utilitaires
// ─────────────────────────────────────────────────────────────

export {
  fetchEmployees,
  fetchEmployee,
  createEmployee,
  updateEmployee,
  deleteEmployee,
  fetchLeaves,
  fetchLeaveBalance,
  createLeave,
  approveLeave,
  rejectLeave,
  fetchPayrollRuns,
  fetchPayrollRun,
  createPayrollRun,
  deletePayrollRun,
  updatePayrollSlip,
  regeneratePayrollRun,
  validatePayrollRun,
  payPayrollRun,
  fetchPayrollParameters,
  setPayrollParameter,
  applyOfficialPayrollDefaults,
};
